import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ApiError, onSessionExpired } from '../lib/api.js';
import { useSession } from '../stores/session.js';

/**
 * Query defaults for an operations tool.
 *
 * `refetchOnWindowFocus` is on here and off in the storefront: an operator
 * leaves the orders grid open all day beside their email, and coming back to
 * stale rows is how two people ship the same parcel.
 */
export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        retry: (attempt, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return attempt < 2;
        },
      },
      mutations: { retry: false },
    },
  });

export const Providers = ({ children }) => {
  const [client] = useState(createQueryClient);
  const signedOut = useSession((state) => state.signedOut);

  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      signedOut();
      client.clear();
    });
    return unsubscribe;
  }, [client, signedOut]);

  return (
    <HelmetProvider>
      <QueryClientProvider client={client}>{children}</QueryClientProvider>
    </HelmetProvider>
  );
};

export default Providers;
